import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { itemAPI, uploadAPI } from '../services/api';
import { X, Upload } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import InfoTooltip from '../components/common/InfoTooltip';
import { recommendBox } from '../utils/boxRecommendation';

const CATEGORIES = [
  'Clothes & Shoes',
  'Toys & Games',
  'Feeding',
  'Feeding (Non-liquid, Non-edible)',
  'Diapering',
  'Bathing & Skincare',
  'Bathing (Non-liquid)',
  'Health & Safety',
  'Nursery',
  'Strollers & Car Seats',
  'Carriers & Wraps',
  'Activity & Entertainment',
  'Books',
  'Blankets & Bedding',
  'Baby Gear',
  'Maternity',
  'Other',
];

const AGE_GROUPS = [
  'Newborn (0-3 months)',
  'Infant (3-12 months)',
  'Toddler (1-3 years)',
  'Preschool (3-5 years)',
  'All Ages',
];

const CONDITIONS = ['New with Tags', 'Like New', 'Good', 'Fair', 'Well Loved'];

const CreateListing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    ageGroup: '',
    condition: '',
    price: '',
    brand: '',
    color: '',
    location: '',
    tags: [],
    isForSale: true,
    sellInPerson: false, // opt out of shipping (local pickup / meetup only)
    itemDimensions: {
      weight: '',
      length: '',
      width: '',
      height: '',
      weightUnit: 'lb',
      dimensionUnit: 'in',
    },
  });

  const [images, setImages] = useState([]);
  const [tagInput, setTagInput] = useState('');

  // Handle text input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle item dimension changes
  const handlePackageDimensionChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      itemDimensions: {
        ...prev.itemDimensions,
        [name]: value,
      },
    }));
  };

  // Handle image selection
  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);

    if (images.length + files.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }

    setLoading(true);
    setError(null);

    // Process each file
    const newImages = await Promise.all(
      files.map(async (file, index) => {
        try {
          let processedFile = file;
          const fileName = file.name.toLowerCase();

          // Convert HEIC/HEIF to JPEG
          if (fileName.endsWith('.heic') || fileName.endsWith('.heif') || file.type === 'image/heic' || file.type === 'image/heif') {
            try {
              console.log(`Converting ${file.name} from HEIC to JPEG...`);

              // Try to dynamically import heic2any
              let heic2any;
              try {
                heic2any = (await import('heic2any')).default;
              } catch (importError) {
                console.error('heic2any library not available:', importError);
                setError(`iPhone photos need to be converted first. Please go to Settings > Camera > Formats and select "Most Compatible", then take a new photo.`);
                return null;
              }

              const convertedBlob = await heic2any({
                blob: file,
                toType: 'image/jpeg',
                quality: 0.9,
              });

              // heic2any might return an array of blobs
              const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

              processedFile = new File(
                [blob],
                file.name.replace(/\.(heic|heif)$/i, '.jpg'),
                { type: 'image/jpeg' }
              );

              console.log(`✅ Successfully converted ${file.name} to JPEG`);
            } catch (conversionError) {
              console.error('HEIC conversion failed:', conversionError);
              setError(`Could not convert ${file.name}. Try changing your iPhone camera settings: Settings > Camera > Formats > "Most Compatible"`);
              return null;
            }
          }

          // Validate it's an image
          if (!processedFile.type.startsWith('image/')) {
            setError(`${file.name} is not a valid image file`);
            return null;
          }

          // Check file size (max 10MB)
          if (processedFile.size > 10 * 1024 * 1024) {
            setError(`${file.name} is too large. Maximum size is 10MB`);
            return null;
          }

          return {
            file: processedFile,
            preview: URL.createObjectURL(processedFile),
            isPrimary: images.length === 0 && index === 0,
            name: processedFile.name,
          };
        } catch (err) {
          console.error('Error processing file:', err);
          setError(`Failed to process ${file.name}: ${err.message}`);
          return null;
        }
      })
    );

    // Filter out null values (failed conversions)
    const validImages = newImages.filter((img) => img !== null);

    if (validImages.length > 0) {
      setImages((prev) => [...prev, ...validImages]);
      setError(null); // Clear any previous errors if we got valid images
    }

    setLoading(false);
    e.target.value = ''; // Reset input
  };

  // Remove image
  const removeImage = (index) => {
    setImages((prev) => {
      const newImages = prev.filter((_, i) => i !== index);
      // If removed image was primary, make first image primary
      if (prev[index].isPrimary && newImages.length > 0) {
        newImages[0].isPrimary = true;
      }
      return newImages;
    });
  };

  // Set primary image
  const setPrimaryImage = (index) => {
    setImages((prev) =>
      prev.map((img, i) => ({
        ...img,
        isPrimary: i === index,
      }))
    );
  };

  // Handle tag input
  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim().toLowerCase())) {
        setFormData((prev) => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim().toLowerCase()],
        }));
      }
      setTagInput('');
    }
  };

  // Remove tag
  const removeTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (images.length === 0) {
      setError('Please upload at least one image');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First, upload all images to Cloudinary
      const uploadedImages = [];

      console.log(`Starting upload of ${images.length} images...`);

      for (let i = 0; i < images.length; i++) {
        const image = images[i];

        // Skip if already uploaded (has real URL, not blob:)
        if (image.fullSize && !image.fullSize.startsWith('blob:')) {
          uploadedImages.push({
            fullSize: image.fullSize,
            thumbnail: image.thumbnail,
            isPrimary: image.isPrimary,
          });
          continue;
        }

        if (image.file) {
          console.log(`Uploading image ${i + 1}/${images.length}: ${image.name}`);

          const uploadFormData = new FormData();
          uploadFormData.append('image', image.file);
          uploadFormData.append('isPrimary', image.isPrimary ? 'true' : 'false');

          try {
            const uploadRes = await uploadAPI.uploadImage(uploadFormData);

            if (uploadRes.data.success) {
              uploadedImages.push({
                fullSize: uploadRes.data.data.fullSize,
                thumbnail: uploadRes.data.data.thumbnail,
                isPrimary: uploadRes.data.data.isPrimary || image.isPrimary,
              });
              console.log(`✅ Image ${i + 1} uploaded successfully`);
            } else {
              throw new Error(`Upload failed for image ${i + 1}`);
            }
          } catch (uploadErr) {
            console.error(`❌ Failed to upload image ${i + 1}:`, uploadErr);
            throw new Error(`Failed to upload image: ${image.name}`);
          }
        }
      }

      if (uploadedImages.length === 0) {
        throw new Error('No images were uploaded successfully');
      }

      console.log(`All ${uploadedImages.length} images uploaded, creating listing...`);

      // Ensure at least one image is primary
      if (!uploadedImages.some((img) => img.isPrimary)) {
        uploadedImages[0].isPrimary = true;
      }

      // Create the baby item with uploaded images
      const itemData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        ageGroup: formData.ageGroup,
        condition: formData.condition,
        price: formData.isForSale ? parseFloat(formData.price) : 0,
        brand: formData.brand?.trim() || undefined,
        color: formData.color?.trim() || undefined,
        location: formData.location?.trim() || undefined,
        tags: formData.tags,
        images: uploadedImages,
        status: 'active',
        isForSale: formData.isForSale,
        // Seller enters the ITEM's dimensions; the backend derives the box
        // (item + cushioning) used for buyer shipping quotes and the seller's
        // box recommendation. In-person-only listings skip this entirely.
        itemDimensions: (!formData.sellInPerson && formData.itemDimensions.weight) ? {
          weight: parseFloat(formData.itemDimensions.weight),
          length: parseFloat(formData.itemDimensions.length) || undefined,
          width: parseFloat(formData.itemDimensions.width) || undefined,
          height: parseFloat(formData.itemDimensions.height) || undefined,
          weightUnit: formData.itemDimensions.weightUnit,
          dimensionUnit: formData.itemDimensions.dimensionUnit,
        } : undefined,
        shippingOptions: {
          localPickup: true,
          shipping: !formData.sellInPerson && !!formData.itemDimensions.weight,
        },
      };

      console.log('Creating item with data:', itemData);

      const res = await itemAPI.createItem(itemData);

      if (res.data.success) {
        console.log('✅ Item created successfully:', res.data.data._id);
        navigate(`/items/${res.data.data._id}`);
      } else {
        throw new Error(res.data.message || 'Failed to create listing');
      }
    } catch (err) {
      console.error('Error creating listing:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create listing');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container" style={{ paddingTop: '40px', paddingBottom: '60px' }}>
      <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '24px' }}>Create New Listing</h1>

        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#c00',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Image Upload Section */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Images * (Max 5)
            </label>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              {images.map((image, index) => (
                <div
                  key={index}
                  style={{
                    position: 'relative',
                    paddingBottom: '100%',
                    border: image.isPrimary ? '3px solid var(--primary)' : '2px solid var(--border)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={image.preview}
                    alt={`Upload ${index + 1}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <X size={16} />
                  </button>

                  {!image.isPrimary && (
                    <button
                      type="button"
                      onClick={() => setPrimaryImage(index)}
                      style={{
                        position: 'absolute',
                        bottom: '8px',
                        left: '8px',
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        cursor: 'pointer',
                      }}
                    >
                      Set Primary
                    </button>
                  )}

                  {image.isPrimary && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '8px',
                        left: '8px',
                        background: 'var(--primary)',
                        color: 'white',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                      }}
                    >
                      Primary
                    </div>
                  )}
                </div>
              ))}

              {images.length < 5 && (
                <label
                  style={{
                    position: 'relative',
                    paddingBottom: '100%',
                    border: '2px dashed var(--border)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--secondary)',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                    }}
                  >
                    <Upload size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                    <div style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>Upload</div>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
            </div>
            <p style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
              Click on an image to set it as primary. First image is primary by default.
            </p>
          </div>

          {/* Title */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="title" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              maxLength={100}
              placeholder="e.g., Baby Carrier - Like New"
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="description" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              maxLength={2000}
              rows={6}
              placeholder="Describe your item in detail..."
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                resize: 'vertical',
              }}
            />
          </div>

          {/* Category and Age Group Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label htmlFor="category" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Category *
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
              >
                <option value="">Select category</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="ageGroup" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Age Group
              </label>
              <select
                id="ageGroup"
                name="ageGroup"
                value={formData.ageGroup}
                onChange={handleChange}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
              >
                <option value="">Select age group</option>
                {AGE_GROUPS.map((age) => (
                  <option key={age} value={age}>
                    {age}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Condition and Price Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label htmlFor="condition" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Condition *
              </label>
              <select
                id="condition"
                name="condition"
                value={formData.condition}
                onChange={handleChange}
                required
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
              >
                <option value="">Select condition</option>
                {CONDITIONS.map((cond) => (
                  <option key={cond} value={cond}>
                    {cond}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="price" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Price * ($)
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                placeholder="25.00"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
              />
            </div>
          </div>

          {/* Brand and Color Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label htmlFor="brand" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Brand
              </label>
              <input
                type="text"
                id="brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                placeholder="e.g., BabyBjörn"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
              />
            </div>

            <div>
              <label htmlFor="color" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Color
              </label>
              <input
                type="text"
                id="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                placeholder="e.g., Blue"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
              />
            </div>
          </div>

          {/* Location */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="location" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Seattle, WA"
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
            />
          </div>

          {/* Tags */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="tags" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Tags
            </label>
            <input
              type="text"
              id="tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleAddTag}
              placeholder="Type a tag and press Enter"
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
            />
            {formData.tags.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      background: 'var(--primary)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                      }}
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Package Dimensions for Shipping */}
          <div style={{ marginBottom: '20px', padding: '20px', background: 'var(--backgroundSecondary)', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '16px', fontSize: '18px' }}>Shipping</h3>

            {/* Opt out of shipping */}
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '12px 14px',
              background: 'var(--cardBackground)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              cursor: 'pointer',
              marginBottom: '16px',
            }}>
              <input
                type="checkbox"
                checked={formData.sellInPerson}
                onChange={(e) => setFormData((prev) => ({ ...prev, sellInPerson: e.target.checked }))}
                style={{ width: '18px', height: '18px', marginTop: '2px' }}
              />
              <span style={{ fontSize: '14px', lineHeight: 1.5 }}>
                <strong>📍 I'll only sell this in person</strong> (local pickup / meetup — no shipping).
                You can skip the package size below.
              </span>
            </label>

            {!formData.sellInPerson && (
            <>
            <p style={{ color: 'var(--textSecondary)', fontSize: '14px', marginBottom: '12px' }}>
              Enter the <strong>item's</strong> size and weight — we'll figure out the right box and
              show buyers an accurate shipping price.
            </p>

            {/* Item (not box) note */}
            <div style={{
              marginBottom: '16px',
              padding: '12px 14px',
              background: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#7c5e10',
              lineHeight: 1.5,
            }}>
              <strong>📏 Measure the item itself</strong> — not a box. We add packing space
              automatically and tell you exactly which box to use. Also make sure your{' '}
              <strong>phone number and address</strong> are set in{' '}
              <a href="/settings" style={{ color: 'var(--primary)', fontWeight: 600 }}>Settings</a>{' '}
              — carriers (USPS) require the seller's phone and email on the label.
            </div>

            <div style={{ marginBottom: '12px', fontSize: '13px' }}>
              <Link to="/measuring-guide" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                📐 Not sure how to measure? See our quick guide →
              </Link>
            </div>

            {/* Weight */}
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="weight" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: '600' }}>
                Item weight (lbs)
                <InfoTooltip text="How heavy the item is on its own. A kitchen or bathroom scale works — round up to the nearest half pound. We add packaging weight automatically." />
              </label>
              <input
                type="number"
                id="weight"
                name="weight"
                value={formData.itemDimensions.weight}
                onChange={handlePackageDimensionChange}
                min="0"
                step="0.1"
                placeholder="e.g., 2.5"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
              />
            </div>

            {/* Dimensions Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label htmlFor="length" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: '600' }}>
                  Length (in)
                  <InfoTooltip align="left" text="The LONGEST side of the item, measured straight across at its widest point." />
                </label>
                <input
                  type="number"
                  id="length"
                  name="length"
                  value={formData.itemDimensions.length}
                  onChange={handlePackageDimensionChange}
                  min="0"
                  step="0.1"
                  placeholder="12"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                />
              </div>

              <div>
                <label htmlFor="width" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: '600' }}>
                  Width (in)
                  <InfoTooltip text="The SHORTER side, measured straight across (front to back) at its widest point." />
                </label>
                <input
                  type="number"
                  id="width"
                  name="width"
                  value={formData.itemDimensions.width}
                  onChange={handlePackageDimensionChange}
                  min="0"
                  step="0.1"
                  placeholder="8"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                />
              </div>

              <div>
                <label htmlFor="height" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: '600' }}>
                  Height (in)
                  <InfoTooltip align="right" text="How TALL the item stands, from the bottom to the top when sitting upright." />
                </label>
                <input
                  type="number"
                  id="height"
                  name="height"
                  value={formData.itemDimensions.height}
                  onChange={handlePackageDimensionChange}
                  min="0"
                  step="0.1"
                  placeholder="6"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)' }}
                />
              </div>
            </div>

            {/* Live box recommendation */}
            {(() => {
              const rec = recommendBox({
                length: formData.itemDimensions.length,
                width: formData.itemDimensions.width,
                height: formData.itemDimensions.height,
                weight: formData.itemDimensions.weight,
                category: formData.category,
              });
              if (!rec) return null;
              return (
                <div style={{
                  marginTop: '16px',
                  padding: '14px 16px',
                  background: 'var(--cardBackground)',
                  border: '1px solid var(--success, #16a34a)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  lineHeight: 1.5,
                }}>
                  <div style={{ fontWeight: 700, marginBottom: '4px' }}>
                    📦 Recommended box: {rec.name}
                  </div>
                  {rec.source && (
                    <div style={{ color: 'var(--textSecondary)' }}>{rec.source}</div>
                  )}
                  {rec.note && (
                    <div style={{ color: 'var(--textSecondary)', marginTop: '4px' }}>{rec.note}</div>
                  )}
                  <div style={{ color: 'var(--textSecondary)', marginTop: '6px' }}>
                    We'll include this in your sale email and dashboard so you know exactly how to ship it.
                  </div>
                </div>
              );
            })()}
            </>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: '600',
            }}
          >
            {loading ? 'Creating Listing...' : 'Create Listing'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateListing;
